import { useEffect, useCallback, useRef } from 'react';
import _ from 'underscore';

import { TIMEOUTS, SELECTORS } from '../constants';

/**
 * Calculates the header height, excluding the mobile menu expansion.
 *
 * @param {HTMLElement|null} headerEl - The header DOM element
 * @param {HTMLElement|null} toggleButtonEl - The mobile menu toggle button element
 * @returns {number} The header height in pixels, excluding mobile menu if expanded
 */
const calculateProjectHeaderHeight = (headerEl, toggleButtonEl) => {
    if (!headerEl) {
        return 0;
    }

    let headerHeight = headerEl.offsetHeight;

    // Check for any expanded or expanding Bootstrap menu state
    const navbarCollapse = headerEl.querySelector('.navbar-collapse.in, .navbar-collapse.collapsing');

    if (navbarCollapse) {
        // Only subtract menu height if we're actually in mobile mode (toggle is visible)
        const isMobileMode = toggleButtonEl && window.getComputedStyle(toggleButtonEl).display !== 'none';

        if (isMobileMode) {
            // Subtract the menu's height to get the "base" header height
            headerHeight = headerHeight - navbarCollapse.offsetHeight;
        }
    }

    return headerHeight;
};


const useFullScreenContainer = (options = {}) => {
    const {
        headerSelector = 'header',
        footerSelector = 'footer',
    } = options;

    const containerRef = useRef(null);

    const updateLayout = useCallback((elements) => {
        const { headerEl, footerEl, toggleButtonEl } = elements;

        const headerHeight = calculateProjectHeaderHeight(headerEl, toggleButtonEl);

        const getEffectiveFooterHeight = () => {
            if (!footerEl) return 0;
            const confirmationFooter = document.querySelector('footer.footer-with-confirmation-link');
            if (confirmationFooter) {
                return confirmationFooter.offsetHeight;
            }
            return footerEl.offsetHeight;
        };

        const footerHeight = getEffectiveFooterHeight();

        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        document.documentElement.style.setProperty('--footer-height', `${footerHeight}px`);

    }, []);

    useEffect(() => {
        const controller = new AbortController();

        const elements = {
            headerEl: document.querySelector(headerSelector),
            footerEl: document.querySelector(footerSelector),
            toggleButtonEl: document.querySelector(SELECTORS.NAVBAR_TOGGLE)
        };

        const boundUpdateLayout = () => updateLayout(elements);

        const debouncedUpdateLayout = _.debounce(boundUpdateLayout, TIMEOUTS.RESIZE_DEBOUNCE);

        const vp = window.visualViewport;
        if (vp) {
            vp.addEventListener('resize', debouncedUpdateLayout, { signal: controller.signal });
        } else {
            window.addEventListener('resize', debouncedUpdateLayout, { passive: true, signal: controller.signal });
        }

        const observer = new ResizeObserver(boundUpdateLayout);
        if (elements.headerEl) observer.observe(elements.headerEl);
        if (elements.footerEl) observer.observe(elements.footerEl);

        boundUpdateLayout();

        return () => {
            controller.abort();
            debouncedUpdateLayout.cancel();
            observer.disconnect();

            document.documentElement.style.removeProperty('--header-height');
            document.documentElement.style.removeProperty('--footer-height');
        };
    }, [updateLayout, headerSelector, footerSelector]);

    return { containerRef };
};

export default useFullScreenContainer;
